import React from 'react';
import {
  TableCellsIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';

export const TemplateManagementGuidePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <TableCellsIcon className="w-8 h-8 text-[#FF5722]" />
              <h1 className="text-3xl font-bold text-gray-900">
                Template Management
              </h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl">
              Learn how to create reusable SMS templates and manage them so users can quickly compose
              messages that follow your organization&apos;s best practices.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Overview steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Part 1
            </h2>
            <p className="text-base font-medium text-gray-900">
              Create a new template
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Use the <span className="font-medium">Create Template</span> page to define reusable
              SMS content.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Part 2
            </h2>
            <p className="text-base font-medium text-gray-900">
              Manage existing templates
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Use <span className="font-medium">Manage Templates</span> to browse, adjust and reuse
              templates.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Part 3
            </h2>
            <p className="text-base font-medium text-gray-900">
              Use templates in SMS flows
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Apply templates when sending Quick SMS and SMS Campaigns to save time and keep
              messaging consistent.
            </p>
          </div>
        </div>

        {/* Create Template section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <PencilSquareIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">
                1. Create a new template (CreateTemplate page)
              </h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                As a super admin, open <span className="font-medium">Template Management</span> and
                navigate to <span className="font-medium">Create Template</span> (
                <span className="font-mono">CreateTemplate.tsx</span>).
              </li>
              <li>
                In <span className="font-medium">Template Name</span>, enter a clear, descriptive
                name such as &quot;Welcome Message&quot; or &quot;Payment Reminder&quot;. This name
                is how users will recognize the template when selecting it.
              </li>
              <li>
                Choose a <span className="font-medium">Category</span> (Marketing, Notifications,
                Reminders, Custom, etc.) to organize templates by use case.
              </li>
              <li>
                In <span className="font-medium">Template Content</span>, type the SMS body you want
                to reuse. Keep it within your character budget and aligned to your compliance rules.
              </li>
              <li>
                Use the <span className="font-medium">Insert Variable</span> dropdown to add dynamic
                placeholders (for example <span className="font-mono">{'{name}'}</span>,{' '}
                <span className="font-mono">{'{date}'}</span>,{' '}
                <span className="font-mono">{'{order_number}'}</span>). These can be replaced with
                real values when sending SMS.
              </li>
              <li>
                Watch the <span className="font-medium">Characters</span> and{' '}
                <span className="font-medium">SMS Count</span> indicators beneath the text area. The
                app calculates SMS segments assuming 160 characters per SMS, helping you estimate
                credit usage.
              </li>
              <li>
                When you&apos;re satisfied, click <span className="font-medium">Create Template</span>.
                The page uses the <span className="font-mono">TemplateContext</span> (
                <span className="font-mono">addTemplate</span>) to store the new template so it can
                be reused in messaging flows.
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              Templates created here become available in the Quick SMS and SMS Campaign &quot;Choose
              template&quot; dropdowns, depending on how your TemplateContext is wired to the API.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Squares2X2Icon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">
                2. Best practices when designing templates
              </h3>
            </div>
            <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
              <li>
                Use clear names and categories so users can find the right template quickly from
                dropdowns in SMS flows.
              </li>
              <li>
                Keep templates short and focused—longer content increases the SMS segment count and
                consumes more credits.
              </li>
              <li>
                Include variables for personalization (names, dates, order numbers) instead of
                hard‑coding details, so the same template can be reused across many customers.
              </li>
              <li>
                When needed, include unsubscribe information (for example via a variable or phrase
                agreed with your SMS provider) to stay compliant with local regulations.
              </li>
              <li>
                Test new templates internally using small Quick SMS sends before rolling them out to
                large campaigns.
              </li>
            </ul>
          </div>
        </div>

        {/* Manage Templates section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <DocumentDuplicateIcon className="w-7 h-7 text-[#FF5722]" />
            <h3 className="text-lg font-semibold text-gray-900">
              3. Manage templates (ManageTemplates page)
            </h3>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            The <span className="font-medium">Manage Templates</span> page (
            <span className="font-mono">ManageTemplates.tsx</span>) lets you review and curate the
            templates available to users.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>
              As a super admin, navigate to <span className="font-medium">Templates &gt; Manage Templates</span>.
              The page shows an overview of existing templates and recommendation examples.
            </li>
            <li>
              Use the <span className="font-medium">Search</span> box to quickly find a template by
              name or content, and the <span className="font-medium">All Industries</span> filter to
              narrow templates by industry or use case.
            </li>
            <li>
              In the <span className="font-medium">Adjust Existing Templates</span> table, review:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>Template name and content.</li>
                <li>Usage metrics (how many times each template has been used, if connected).</li>
                <li>Edit/delete actions for each template row.</li>
              </ul>
            </li>
            <li>
              Click <span className="font-medium">Create Template</span> from this page to jump
              directly to the Create Template flow described above.
            </li>
            <li>
              In the <span className="font-medium">Template Recommendations</span> section, review
              example templates with engagement percentages. These examples can help you design your
              own high‑performing templates tailored to your industry.
            </li>
            <li>
              Use the <span className="font-medium">Use Template</span> action on a recommendation to
              copy its content into your own template workflow (depending on how your environment is
              wired, this may prefill a new template for you to customize).
            </li>
          </ol>
          <p className="mt-4 text-xs text-gray-500">
            The current implementation of `ManageTemplates` uses mock data; when connected to the
            backend, this page will surface live template usage and recommendations driven by your
            actual SMS activity.
          </p>
        </div>

        {/* Using templates in SMS flows */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <TableCellsIcon className="w-7 h-7 text-[#FF5722]" />
            <h3 className="text-lg font-semibold text-gray-900">
              4. Use templates when sending SMS
            </h3>
          </div>
          <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
            <li>
              On the <span className="font-medium">Quick SMS</span> page, use the{' '}
              <span className="font-medium">Choose template</span> dropdown above the message box to
              load a saved template into the SMS body.
            </li>
            <li>
              On the <span className="font-medium">SMS Campaign</span> page, likewise choose a
              template before customizing campaign‑specific details or variables.
            </li>
            <li>
              After choosing a template, you can still edit the message for that particular send
              without changing the underlying template in Template Management.
            </li>
            <li>
              Keep an eye on the symbols and SMS count indicators in these send flows; templates that
              are too long can significantly increase credit consumption when applied to large
              recipient lists.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TemplateManagementGuidePage;


